/**
 * 브랜드 입점 신청 관련 API 서비스
 *
 * API 클라이언트 인스턴스를 인자로 받아 호출 (DI 패턴)
 * shared 레이어: config, workspace-pkg만 import 가능
 */

import type { ApiClient } from "@/shared/api/client";

// ── 중복 확인 파라미터 ──

export interface CheckDuplicationParams {
  name?: string;
  mobileNo?: string;
  registrationNo?: string;
  email?: string;
}

// ── 사업자 등록증 업로드 응답 ──

export interface LicenseUploadResponse {
  url: string;
}

// ── 입점 신청 조회 응답 ──

/** ✅ verified against legacy backend 2026-04-19 — GET /brandApply/my_requested */
export interface BrandApply {
  id: number;
  name: string;
  email: string;
  mobileNo: string | null;
  registrationNo: string;
  businessName: string;
  status: "requested" | "approved" | "rejected";
  mainProdLink: string | null;
  mainProdRetailPrice: number | null;
  mainProdWholesalePrice: number | null;
  mainProdMinOrderCnt: number | null;
  averageDiscountRate: number | null;
  businessRegistrationImageUrl: string | null;
  homepagUrl: string | null;
  description: string;
  detailContent: string | null;
  memo: string | null;
  recommName: string | null;
  recommFl: "y" | "n" | null;
  inviterType: "user" | "brand" | null;
  inviterId: number | null;
  createdAt: string | null;
  updatedAt: string | null;
}

export interface MyRequestedResponse {
  apply: BrandApply | null;
}

// ── 입점 신청 요청 ──

/** ✅ verified against legacy backend 2026-04-19 — POST /brandApply (req.body → BrandApply.create) */
export interface BrandApplyRequest {
  name: string;
  email: string;
  mobileNo?: string;
  registrationNo: string;
  businessName: string;
  description: string;
  mainProdLink?: string;
  mainProdRetailPrice?: number;
  mainProdWholesalePrice?: number;
  mainProdMinOrderCnt?: number;
  averageDiscountRate?: number;
  businessRegistrationImageUrl?: string;
  homepagUrl?: string;
  detailContent?: string;
  recommName?: string;
  recommFl?: "y" | "n";
}

// ── 초대 ──

/** ✅ verified against legacy backend 2026-04-19 — GET /brandApply/by_inviter (returns BrandApply[] filtered by inviterId) */
export interface Invite {
  id: number;
  name: string;
  email: string;
  mobileNo: string | null;
  registrationNo: string;
  businessName: string;
  status: "requested" | "approved" | "rejected";
  inviterType: "user" | "brand" | null;
  inviterId: number | null;
  createdAt: string | null;
  updatedAt: string | null;
}

// ── 카운터 ──

export interface CountResponse {
  count: number;
}

// ── 서비스 팩토리 ──

export function createBrandApplyService(client: ApiClient) {
  return {
    /** 중복 확인 GET /brands/check_duplication */
    checkDuplication: (params: CheckDuplicationParams) =>
      client.get<Record<string, string>>("/brands/check_duplication", {
        params: {
          ...(params.name !== undefined && { name: params.name }),
          ...(params.mobileNo !== undefined && { mobileNo: params.mobileNo }),
          ...(params.registrationNo !== undefined && { registrationNo: params.registrationNo }),
          ...(params.email !== undefined && { email: params.email }),
        },
      }),

    /** 사업자 등록증 업로드 POST /brands/license */
    uploadLicense: (formData: FormData) =>
      client.post<LicenseUploadResponse>("/brands/license", formData),

    /** 내 입점 신청 조회 GET /brandApply/my_requested */
    getMyRequested: (params: { type: string; mobileNo: string }) =>
      client.get<MyRequestedResponse>("/brandApply/my_requested", {
        params: { type: params.type, mobileNo: params.mobileNo },
      }),

    /** 입점 신청 POST /brandApply */
    apply: (data: BrandApplyRequest) => client.post<null>("/brandApply", data),

    /** 초대 목록 조회 GET /brandApply/by_inviter */
    getInvites: () => client.get<Invite[]>("/brandApply/by_inviter"),

    /** 바이어(유저) 수 조회 GET /users/count-users */
    getUserCount: () => client.get<CountResponse>("/users/count-users"),

    /** 브랜드 수 조회 GET /brands/count-brands */
    getBrandCount: () => client.get<CountResponse>("/brands/count-brands"),
  };
}
